'use client';

import { useCallback, useMemo } from 'react';
import { useGetProviderCohortsQuery } from 'src/api/redux/providerCohorts';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';
import { useGetAdvancedSearchesQuery } from 'src/api/redux/advancedSearches';
import useGetAccountFolders from 'src/hooks/accountFolders/useGetAccountFolders';
import type { AccountFolder } from 'src/api/redux/accountFolders';
import type {
  CodeSetItem,
  CodeSetFolder,
  UseCodeSetSelectorDataConfig,
  UseCodeSetSelectorDataResult,
} from '../types';

const DEFAULT_FILTER_SET_ARGS = { type: 'CODE' } as const;

/**
 * A 403 means the user's role simply isn't issued that capability (e.g.
 * `read.advanced_searches.personal`), not that the request failed. Treat it as
 * "this category is unavailable to you" so an optional feature never surfaces
 * as a permanent error banner. See MKT-659.
 */
const isPermissionError = (error: unknown) =>
  (error as { status?: number | string })?.status === 403;

/**
 * Cohort, filter set and advanced search ids come from separate tables and can
 * collide, so folder membership is keyed by kind and id together.
 */
const toMembershipKey = (kind: CodeSetItem['kind'], id: number) =>
  `${kind}:${id}`;

/**
 * Shared data layer for the code-set / volume selectors (Discovery saved-volumes
 * dropdown, Lists column selector, profile selector).
 *
 * It owns the logic that was previously duplicated — and drifted — across those
 * components: fetching cohorts + filter sets + folders, detecting which items
 * live in folders, resolving each folder cohort's status against the
 * authoritative live cohorts list, and applying the deactivated policy. Consumers
 * keep their own scope-bucketing and rendering.
 *
 * Note: the temporary `?status=ALL` behavior lives in the `getProviderCohorts`
 * RTK endpoint (shared by every cohort consumer) and will be removed once the
 * MKT-255 API default ships.
 */
export const useCodeSetSelectorData = (
  config: UseCodeSetSelectorDataConfig = {}
): UseCodeSetSelectorDataResult => {
  const {
    filterSetArgs = DEFAULT_FILTER_SET_ARGS,
    cohortsQueryOptions,
    filterSetType,
    matchFolderItemScope = true,
    deactivatedPolicy = 'exclude',
    includeAdvancedSearches = false,
    advancedSearchesQueryOptions,
  } = config;

  const {
    data: cohortsData,
    error: cohortsError,
    isLoading: cohortsLoading,
    refetch: refetchCohorts,
  } = useGetProviderCohortsQuery(undefined, cohortsQueryOptions);

  const {
    data: filterSetsData,
    error: filterSetsError,
    isLoading: filterSetsLoading,
    refetch: refetchFilterSets,
  } = useGetFilterSetsQuery(filterSetArgs);

  const {
    data: accountFoldersData,
    error: foldersError,
    isLoading: foldersLoading,
    mutate: mutateAccountFolders,
    refetch: refetchAccountFolders,
  } = useGetAccountFolders();

  const {
    data: advancedSearchesData,
    error: advancedSearchesError,
    isLoading: advancedSearchesLoading,
    refetch: refetchAdvancedSearches,
  } = useGetAdvancedSearchesQuery(undefined, {
    ...advancedSearchesQueryOptions,
    skip: !includeAdvancedSearches,
  });

  /**
   * A 403 has to remove the category's items, not just its error. The folder
   * payload nests `advanced_searches` regardless of the caller's advanced-search
   * permission (it is gated on the folders endpoint's own permission), and
   * `getFolderItems` falls back to that payload when there is no live record —
   * so suppressing only the error would leave folder-embedded advanced searches
   * rendering silently for a role that cannot see them.
   */
  const showAdvancedSearches =
    includeAdvancedSearches && !isPermissionError(advancedSearchesError);

  // Authoritative cohort record by id — the single source of truth for status.
  const cohortsById = useMemo(() => {
    const map = new Map<number, any>();
    cohortsData?.results?.forEach((cohort: any) => map.set(cohort.id, cohort));
    return map;
  }, [cohortsData]);

  const filterSetsById = useMemo(() => {
    const map = new Map<number, any>();
    filterSetsData?.results?.forEach((fs: any) => map.set(fs.id, fs));
    return map;
  }, [filterSetsData]);

  const isDeactivated = useCallback(
    (status?: string) =>
      deactivatedPolicy === 'exclude' && status === 'DEACTIVATED',
    [deactivatedPolicy]
  );

  const toCohortItem = useCallback(
    (cohort: any): CodeSetItem => ({
      id: cohort.id,
      label: cohort.label,
      kind: 'cohort',
      scope: cohort.scope,
      status: cohort.status,
      raw: cohort,
    }),
    []
  );

  const toFilterSetItem = useCallback(
    (filterSet: any): CodeSetItem => ({
      id: filterSet.id,
      label: filterSet.label,
      kind: 'filterSet',
      scope: filterSet.scope,
      status: filterSet.status,
      raw: filterSet,
    }),
    []
  );

  const advancedSearchesById = useMemo(() => {
    const map = new Map<number, any>();
    if (!showAdvancedSearches) return map;
    advancedSearchesData?.results?.forEach((search: any) =>
      map.set(search.id, search)
    );
    return map;
  }, [advancedSearchesData, showAdvancedSearches]);

  const toAdvancedSearchItem = useCallback(
    (search: any): CodeSetItem => ({
      id: search.id,
      label: search.label,
      kind: 'advancedSearch',
      scope: search.scope,
      status: search.status,
      raw: search,
    }),
    []
  );

  // Resolve a single folder's scope-matched, status-resolved items.
  const getFolderItems = useCallback(
    (folder: AccountFolder): CodeSetItem[] => {
      if (!folder.scope) return [];
      const expectedScope = folder.scope.toLowerCase();
      const items: CodeSetItem[] = [];

      folder.provider_cohorts?.forEach((cohort) => {
        if (matchFolderItemScope && cohort.scope?.toLowerCase() !== expectedScope)
          return;
        // Prefer the authoritative live record (esp. status); the folder payload
        // status can be stale.
        const live = cohortsById.get(cohort.id);
        const resolved = live ?? cohort;
        const status = live?.status ?? cohort.status;
        if (isDeactivated(status)) return;
        items.push({ ...toCohortItem(resolved), status });
      });

      folder.filter_sets?.forEach((filterSet: any) => {
        if (filterSetType && filterSet.type !== filterSetType) return;
        const full = filterSetsById.get(filterSet.id);
        if (matchFolderItemScope) {
          // Require the full record and a matching scope.
          if (!full || full.scope?.toLowerCase() !== expectedScope) return;
          items.push(toFilterSetItem(full));
        } else {
          // Include regardless of scope, falling back to the folder payload.
          items.push(toFilterSetItem(full ?? filterSet));
        }
      });

      if (showAdvancedSearches) {
        folder.advanced_searches?.forEach((search: any) => {
          if (matchFolderItemScope && search.scope?.toLowerCase() !== expectedScope)
            return;
          const live = advancedSearchesById.get(search.id);
          const resolved = live ?? search;
          items.push(toAdvancedSearchItem(resolved));
        });
      }

      return items;
    },
    [
      cohortsById,
      filterSetsById,
      advancedSearchesById,
      isDeactivated,
      toCohortItem,
      toFilterSetItem,
      toAdvancedSearchItem,
      matchFolderItemScope,
      filterSetType,
      showAdvancedSearches,
    ]
  );

  // Items already shown inside a scope-matching folder (excluded from ungrouped).
  const keysInMatchingFolders = useMemo(() => {
    const keys = new Set<string>();
    accountFoldersData?.results?.forEach((folder: AccountFolder) => {
      if (!folder.scope) return;
      const expectedScope = folder.scope.toLowerCase();
      folder.provider_cohorts?.forEach((cohort) => {
        if (!matchFolderItemScope || cohort.scope?.toLowerCase() === expectedScope)
          keys.add(toMembershipKey('cohort', cohort.id));
      });
      folder.filter_sets?.forEach((filterSet: any) => {
        // Keep this consistent with getFolderItems so an item isn't claimed here
        // (and thus removed from ungrouped) while being filtered out of the folder.
        if (filterSetType && filterSet.type !== filterSetType) return;
        if (!matchFolderItemScope) {
          keys.add(toMembershipKey('filterSet', filterSet.id));
          return;
        }
        const full = filterSetsById.get(filterSet.id);
        if (full && full.scope?.toLowerCase() === expectedScope) {
          keys.add(toMembershipKey('filterSet', filterSet.id));
        }
      });

      if (showAdvancedSearches) {
        folder.advanced_searches?.forEach((search: any) => {
          if (!matchFolderItemScope || search.scope?.toLowerCase() === expectedScope)
            keys.add(toMembershipKey('advancedSearch', search.id));
        });
      }
    });
    return keys;
  }, [accountFoldersData, filterSetsById, matchFolderItemScope, filterSetType, showAdvancedSearches]);

  const ungroupedItems = useMemo(() => {
    const items: CodeSetItem[] = [];

    cohortsData?.results?.forEach((cohort: any) => {
      if (isDeactivated(cohort.status)) return;
      if (keysInMatchingFolders.has(toMembershipKey('cohort', cohort.id))) return;
      items.push(toCohortItem(cohort));
    });

    filterSetsData?.results?.forEach((filterSet: any) => {
      if (filterSetType && filterSet.type !== filterSetType) return;
      if (keysInMatchingFolders.has(toMembershipKey('filterSet', filterSet.id)))
        return;
      items.push(toFilterSetItem(filterSet));
    });

    if (showAdvancedSearches) {
      advancedSearchesData?.results
        ?.filter((search: any) => search.status === 'READY')
        ?.forEach((search: any) => {
          if (
            keysInMatchingFolders.has(toMembershipKey('advancedSearch', search.id))
          )
            return;
          items.push(toAdvancedSearchItem(search));
        });
    }

    return items;
  }, [
    cohortsData,
    filterSetsData,
    advancedSearchesData,
    keysInMatchingFolders,
    isDeactivated,
    toCohortItem,
    toFilterSetItem,
    toAdvancedSearchItem,
    filterSetType,
    showAdvancedSearches,
  ]);

  const folders = useMemo<CodeSetFolder[]>(() => {
    return (
      accountFoldersData?.results?.map((folder: AccountFolder) => ({
        ...folder,
        items: getFolderItems(folder),
      })) ?? []
    );
  }, [accountFoldersData, getFolderItems]);

  // A rejected RTK Query cache entry is never retried on its own (nothing opts
  // into refetchOnMountOrArgChange/onFocus), so consumers need a way to recover
  // from a transient failure without a full page reload.
  const refetch = useCallback(() => {
    refetchCohorts();
    refetchFilterSets();
    refetchAccountFolders();
    if (includeAdvancedSearches) refetchAdvancedSearches();
  }, [
    refetchCohorts,
    refetchFilterSets,
    refetchAccountFolders,
    refetchAdvancedSearches,
    includeAdvancedSearches,
  ]);

  const error = useMemo(() => {
    const errors = [
      cohortsError,
      filterSetsError,
      foldersError,
      advancedSearchesError,
    ];
    return errors.find((err) => err && !isPermissionError(err));
  }, [cohortsError, filterSetsError, foldersError, advancedSearchesError]);

  return {
    ungroupedItems,
    folders,
    getFolderItems,
    isLoading: cohortsLoading || filterSetsLoading || foldersLoading || (includeAdvancedSearches && advancedSearchesLoading),
    error,
    refetch,
    raw: { cohortsData, filterSetsData, advancedSearchesData, accountFoldersData, mutateAccountFolders },
  };
};
